import { ApolloError } from '@apollo/client'
import {
  IRoutingProjectDatasetLocation,
  IRoutingProjectPlan,
  TDatasetLocationsGeojson
} from 'features/RoutingProjects/types'
import { RoutingProjectPlanOptimizationSkippedLocationReason } from 'gql/generated/graphql'
import { isNumber } from 'lodash'
import { ReactNode } from 'react'
import { defineMessages, IntlShape } from 'react-intl'
import { adjustMapBounds, IMapPosition } from 'utils/google-maps'

export const mapDatasetLocationsToGeojson = (locations: IRoutingProjectDatasetLocation[]): TDatasetLocationsGeojson => {
  const locationMap = new Map<
    string,
    {
      label: string
      stopIndex: number
      id: string
      coordinates: {
        latitude: number
        longitude: number
      }
    }[]
  >()

  locations.forEach((location, index) => {
    const key = `${location.coordinates?.longitude},${location.coordinates?.latitude}`
    const entry = { label: location.label, stopIndex: index, id: location.id, coordinates: location.coordinates }

    if (!locationMap.has(key)) {
      locationMap.set(key, [])
    }
    locationMap.get(key)?.push(entry)
  })

  const features = Array.from(locationMap.entries()).map(([coordinatesAsKey, stops]) => {
    const [longitude, latitude] = coordinatesAsKey.split(',').map(Number)

    return {
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [longitude, latitude]
      },
      properties: {
        id: coordinatesAsKey,
        stops: stops.map((stop) => ({
          label: stop.label,
          stopIndex: stop.stopIndex,
          stopId: stop.id
        }))
      }
    }
  })

  return {
    type: 'FeatureCollection' as const,
    features
  }
}

export const mapOptimizedRouteToGeojson = (
  routes: IRoutingProjectPlan['optimization']['routes'],
  skippedLocations: IRoutingProjectPlan['optimization']['skippedLocations'],
  constraints: IRoutingProjectPlan['constraints']
): TDatasetLocationsGeojson => {
  const locationMap = new Map<
    string,
    {
      label: string
      stopIndex: number
      stopId: string
      coordinates: {
        latitude: number
        longitude: number
      }
      rgbColor?: string
      isOrigin?: boolean
      isDestination?: boolean
      skippedReasons?: RoutingProjectPlanOptimizationSkippedLocationReason[]
    }[]
  >()

  routes.forEach((route) => {
    route.stops.forEach(({ location }, index) => {
      const key = `${location.coordinates.longitude},${location.coordinates.latitude}`
      const entry = {
        label: location.label,
        stopIndex: index,
        stopId: location.id,
        coordinates: location.coordinates,
        rgbColor: route.rgbColor,
        isOrigin: constraints.origin?.id === location.id,
        isDestination: constraints.destination?.id === location.id
      }

      if (!locationMap.has(key)) {
        locationMap.set(key, [])
      }
      locationMap.get(key)?.push(entry)
    })
  })

  skippedLocations.forEach((skippedLocation, locationIndex) => {
    const location = skippedLocation.location
    const key = `${location.coordinates.longitude},${location.coordinates.latitude}`
    const entry = {
      label: location.label,
      stopIndex: locationIndex,
      stopId: location.id,
      coordinates: location.coordinates,
      skippedReasons: skippedLocation.reasons
    }

    if (!locationMap.has(key)) {
      locationMap.set(key, [])
    }
    locationMap.get(key)?.push(entry)
  })

  const features = Array.from(locationMap.entries()).map(([coordinatesAsKey, stops]) => {
    const [longitude, latitude] = coordinatesAsKey.split(',').map(Number)

    return {
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [longitude, latitude]
      },
      properties: {
        id: coordinatesAsKey,
        stops
      }
    }
  })
  return {
    type: 'FeatureCollection' as const,
    features
  }
}

const csvErrorMessages = defineMessages({
  genericError: {
    id: 'routingProjects.csv.errors.genericError',
    defaultMessage: 'Data in the file does not have the right format. {br} {rowError}'
  },
  rowError: {
    id: 'routingProjects.csv.errors.rowError',
    defaultMessage: 'Error in row: {rowNumber}'
  }
})

export const hasCSVError = (error: ApolloError | undefined): boolean => {
  return isNumber(error?.graphQLErrors?.[0]?.extensions?.line)
}

export const getCSVErrors = (error: ApolloError | undefined, intl: IntlShape): ReactNode => {
  const rowNumber = error?.graphQLErrors?.[0]?.extensions?.line

  return intl.formatMessage(csvErrorMessages.genericError, {
    br: <br />,
    rowError: rowNumber > 0 ? intl.formatMessage(csvErrorMessages.rowError, { rowNumber }) : ''
  })
}

export const adjustMapBoundsByRoutes = ({
  map,
  routes
}: {
  map: google.maps.Map
  routes: IRoutingProjectPlan['optimization']['routes']
}): void => {
  const records = routes.reduce((acc: IMapPosition[], route) => {
    return [
      ...acc,
      ...route.stops.map(({ location }) => {
        return {
          lat: location.coordinates.latitude,
          lng: location.coordinates.longitude
        }
      })
    ]
  }, [])
  adjustMapBounds(records, map)
}
